// Boot-restore window — the "paint restored state at rest" primitive.
//
// A refresh restores last-known content synchronously (persisted Query cache +
// transcript-tail seed + stage restore), but every entrance animation still
// treated that first paint as a fresh arrival: framer block entrances replayed,
// the brief-dock tab slid in, CTAs rose, the cwait rows re-staggered. The fix
// is ONE global signal: `data-boot-restore` on <html>, set BEFORE the first
// render (main.tsx module scope) and cleared the moment the boot stops being a
// restore. While the attribute is present:
//   - the CSS gate in globals.css ("boot-restore gate") disables the known
//     ENTRANCE animations/transitions (never ambient infinite loops — freezing
//     those adds a start-stutter when the flag clears);
//   - framer-driven mounts read `bootRestoreInitial(...)` for their `initial`
//     prop (`initial={false}` = paint at rest).
//
// CLEAR TIMING — first of:
//   (a) bootstrap settle: workspace-app's mount bootstrap, after the restored
//       stage/workspace is applied, via double-rAF — so the post-GET restored
//       elements' first paint is still suppressed, then motion re-enables
//       (a first-rAF clear would MISS the brief-dock elements, which mount
//       only after the async bootstrap GET);
//   (b) the first live Pusher `agent.stream` event (applyStreamEvent) — a
//       live-arriving element must render with motion enabled;
//   (c) a safety setTimeout in main.tsx — a hung bootstrap must not leave the
//       app motion-dead forever.
// All three race; clear is idempotent so the losers are no-ops.
//
// Storage-free and synchronous by design: the flag lives on the DOM root so
// CSS can key off it without JS round-trips, and reads are plain attribute
// checks. Guarded for the node test env (no document) — degrade, never throw.
//
// REACTIVITY: the clears all run OUTSIDE React (double-rAF, a Pusher fold, a
// setTimeout), so a component whose RENDER OUTPUT depends on the flag (not a
// mount-time snapshot) must subscribe via `useBootRestore()` — a bare
// `isBootRestore()` in render goes stale when the last committed render
// happened inside the window (the frozen "Bringing your shortlist back…"
// spinner on a zero-recommendation restore). Subscribers are notified only on
// real open↔closed transitions, so the per-event idempotent clears stay free.

import { useSyncExternalStore } from "react";

const ATTR = "data-boot-restore";

type BootRestoreListener = () => void;
const listeners = new Set<BootRestoreListener>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** Subscribe to open↔closed transitions of the boot-restore window. Returns
 * the unsubscribe. Fires only on actual transitions — the racing idempotent
 * clears' losers notify nobody. */
export function subscribeBootRestore(listener: BootRestoreListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function rootElement(): Element | undefined {
  // `document` is absent in the node test env; degrade to a no-op there.
  return typeof document !== "undefined" ? document.documentElement : undefined;
}

/** Open the boot-restore window. Called as the FIRST side effect in main.tsx,
 * before createRoot().render() — so the very first paint sees the attribute. */
export function markBootRestore(): void {
  const root = rootElement();
  if (!root || root.hasAttribute(ATTR)) return;
  root.setAttribute(ATTR, "");
  notify();
}

/** Close the boot-restore window (idempotent — the settle/live/safety clears
 * race and the losers no-op). Elements mounting after this animate normally. */
export function clearBootRestore(): void {
  const root = rootElement();
  if (!root || !root.hasAttribute(ATTR)) return;
  root.removeAttribute(ATTR);
  notify();
}

/** Is the boot-restore window still open? */
export function isBootRestore(): boolean {
  return rootElement()?.hasAttribute(ATTR) ?? false;
}

/** Reactive read of the boot-restore window for RENDER-time consumers: the
 * clears run outside React, so this is the only read that re-renders when the
 * window closes (vs `isBootRestore()`, a point-in-time snapshot for effects /
 * imperative code, and `bootRestoreInitial`, a deliberate mount-time gate). */
export function useBootRestore(): boolean {
  return useSyncExternalStore(subscribeBootRestore, isBootRestore);
}

/** Framer `initial` gate for mount entrances: the variant name normally,
 * `false` (paint at rest, no entrance) during the boot-restore window.
 *
 * Deliberately read during RENDER, not reactive state: framer only consults
 * `initial` at MOUNT, and we want exactly that mount-time snapshot — the flag
 * is set before the first render and cleared outside render, so a block
 * restored during boot paints at rest while a block added after the clear
 * animates. Re-rendering on clear would retroactively animate settled blocks. */
export function bootRestoreInitial<V extends string>(variant: V): V | false {
  return isBootRestore() ? false : variant;
}
